'use client';

import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Check from 'lucide-react/dist/esm/icons/check';
import X from 'lucide-react/dist/esm/icons/x';
import Equal from 'lucide-react/dist/esm/icons/equal';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { cn } from 'lib/utils';

// Draggable Row Component
const DraggableRow = ({ row, children, isDragging, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: row.original.order,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...props}
      className={cn(props.className, (isDragging || isSortableDragging) && 'relative z-50')}
      {...attributes}
      {...listeners}
    >
      <td className="px-4 py-4 w-8">
        <div className="cursor-grab hover:cursor-grabbing p-1 hover:bg-gray-200 rounded">
          <Equal />
        </div>
      </td>
      {children}
    </tr>
  );
};

const DraggableDataTable = forwardRef(
  (
    {
      columns,
      data,
      onRowClick,
      onRowUpdate,
      onReorder, // New prop for handling reorder
      vehicleTypes = [],
      locations = [],
    },
    ref,
  ) => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({});
    const [rowSelection, setRowSelection] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editData, setEditData] = useState({});
    const [tableData, setTableData] = useState(data);
    const [hasChanges, setHasChanges] = useState(false);

    // Update tableData when data prop changes
    React.useEffect(() => {
      setTableData(data);
      setHasChanges(false);
    }, [data]);

    // Enhanced columns with drag handle
    const enhancedColumns = [
      {
        id: 'drag-handle',
        header: '',
        cell: () => null, // The drag handle is rendered in DraggableRow
        size: 40,
      },
      ...columns,
    ];

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    const table = useReactTable({
      data: tableData,
      columns: enhancedColumns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel(),
      onColumnVisibilityChange: setColumnVisibility,
      onRowSelectionChange: setRowSelection,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
      },
    });

    const handleDragEnd = (event) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = tableData.findIndex((item) => item.order === active.id);
        const newIndex = tableData.findIndex((item) => item.order === over.id);

        const newData = arrayMove(tableData, oldIndex, newIndex);

        // Update orderIds to maintain sequential order
        const reorderedData = newData.map((item, index) => ({
          ...item,
          order: index + 1,
        }));

        setTableData(reorderedData);
        setHasChanges(true);

        // Call the onChange prop if provided
        if (onReorder) {
          onReorder(reorderedData);
        }
      }
    };

    const handleSaveOrder = () => {
      // This function should be called when user wants to save the changes
      // You can add your save logic here or call a prop function
      setHasChanges(false);
      console.log('Saving new order:', tableData);
    };

    const handleResetOrder = () => {
      setTableData(data);
      setHasChanges(false);
    };

    const handleSaveEdit = () => {
      if (onRowUpdate && editingRow) {
        const rowData = tableData.find((row) => row.id === editingRow);
        onRowUpdate(rowData, editData);
      }
      setEditingRow(null);
      setEditData({});
    };

    const handleCancelEdit = () => {
      setEditingRow(null);
      setEditData({});
    };

    const handleInputChange = (columnId, value) => {
      setEditData((prev) => ({ ...prev, [columnId]: value }));
    };

    const renderEditableCell = useCallback(
      (cell, row) => {
        const columnId = cell.column.id;
        const currentValue =
          editData[columnId] !== undefined ? editData[columnId] : cell.getValue();

        if (columnId === 'actions') {
          return (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveEdit}
                className="h-6 w-6 p-0 cursor-pointer bg-green-500 hover:bg-green-600"
              >
                <Check className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEdit}
                className="h-6 w-6 p-0 cursor-pointer hover:bg-gray-100"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          );
        }
      },
      [editData, vehicleTypes, locations],
    );

    useImperativeHandle(ref, () => ({
      setEditingRow: (rowId, rowData) => {
        setEditingRow(rowId);
        setEditData(rowData);
      },
      saveOrder: handleSaveOrder,
      resetOrder: handleResetOrder,
      getReorderedData: () => tableData,
    }));

    return (
      <div className="w-full">
        {/* Save/Reset buttons - only show when there are changes */}
        {hasChanges && (
          <div className="mb-4 flex gap-2">
            <Button onClick={handleSaveOrder} className="bg-blue-600 hover:bg-blue-700">
              Save Order
            </Button>
            <Button onClick={handleResetOrder} variant="outline">
              Reset Order
            </Button>
          </div>
        )}

        <div className="rounded-md">
          <div className="overflow-x-auto border border-[#e8e8e8] rounded-[10px]">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table className="w-full text-sm border-collapse">
                <thead className="bg-[color:var(--brand-primary)] text-white">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="h-[56px]">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-4 py-3 font-semibold text-[14px] font-[600] font-poppins',
                            header.column.id === 'actions' ? 'text-right pl-4' : 'text-left',
                            header.column.id === 'drag-handle' ? 'w-8' : '',
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  <SortableContext
                    items={tableData.map((item) => item.order)}
                    strategy={verticalListSortingStrategy}
                  >
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row, rowIndex) => (
                        <DraggableRow
                          key={row.original.order}
                          row={row}
                          className={cn(
                            'border-b-[0px] transition-colors',
                            editingRow === row.original.id
                              ? 'bg-blue-50 border-blue-200'
                              : 'hover:bg-gray-100',
                            rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          )}
                          onClick={() =>
                            editingRow !== row.original.id && onRowClick?.(row.original)
                          }
                        >
                          {row.getVisibleCells().map((cell) => {
                            // Skip the drag handle cell as it's rendered in DraggableRow
                            if (cell.column.id === 'drag-handle') return null;

                            return (
                              <td
                                key={cell.id}
                                className="text-[16px] font-normal font-poppins px-4 py-4 sm:max-w-[600px]"
                              >
                                {editingRow === row.original.id
                                  ? renderEditableCell(cell, row)
                                  : flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                        </DraggableRow>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={enhancedColumns.length} className="h-24 text-center">
                          No results.
                        </td>
                      </tr>
                    )}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          </div>
        </div>
      </div>
    );
  },
);

DraggableDataTable.displayName = 'DraggableDataTable';

export default DraggableDataTable;
